/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { type PropsWithChildren, useEffect, useState } from 'react';

import { Card } from '@coursera/cds-core';

const HIGHLIGHT_DURATION_MS = 2000;

const styles = {
  card: css`
    &.cds-Card-base .cds-Card-innerCard {
      padding: var(--cds-spacing-200);
    }
  `,
  // One-shot flash for deep-linked cards: blue outline that fades out on mount.
  highlighted: css`
    animation: sectionCardHighlight ${HIGHLIGHT_DURATION_MS}ms ease-out;

    @keyframes sectionCardHighlight {
      0%,
      40% {
        box-shadow: 0 0 0 2px var(--cds-color-emphasis-primary-background-default);
      }

      100% {
        box-shadow: 0 0 0 2px transparent;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
};

type Props = PropsWithChildren<{
  // When the page loads with this location hash (e.g. a deep link like '#sound-effects'), the card
  // flashes once on mount to draw the eye.
  highlightOnHash?: string;
}>;

const SectionCard = ({ children, highlightOnHash }: Props) => {
  const [highlighted, setHighlighted] = useState(false);

  // Read the hash after mount (reading it during render isn't SSR-safe). SoundEffectsField defers
  // stripping the hash to a rAF, so it's still present when this effect runs.
  useEffect(() => {
    if (highlightOnHash && window.location.hash === highlightOnHash) {
      setHighlighted(true);
    }
  }, [highlightOnHash]);

  return (
    <Card css={[styles.card, highlighted && styles.highlighted]} interactive={false}>
      {children}
    </Card>
  );
};

export default SectionCard;
